import { useState } from "react";
import { useAuth, apiClient } from "../App";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { User, Mail, Calendar, Download, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState("");
  const [deleting, setDeleting] = useState(false);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await apiClient.get('/account/export');
      
      // Create blob and download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.download = `cbaiq_export_${date}.json`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Export complete! Your data has been downloaded.");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmed || deleteTyped !== "DELETE") {
      toast.error("Please confirm deletion by checking the box and typing DELETE");
      return;
    }

    setDeleting(true);
    try {
      await apiClient.delete('/account');
      
      toast.success("Account deleted successfully");
      
      // Logout and redirect
      setTimeout(() => {
        logout();
        navigate("/", { replace: true });
      }, 1000);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete account. Please contact support.");
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2" data-testid="settings-title">
            Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your account
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-card border border-border rounded-md p-6 mb-6">
          <h2 className="font-medium mb-4">Profile Information</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                <User className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium" data-testid="user-name">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                <Mail className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium" data-testid="user-email">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center">
                <Calendar className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Member Since</p>
                <p className="font-medium" data-testid="user-created">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Section */}
        <div className="bg-card border border-border rounded-md p-6 mb-6">
          <h2 className="font-medium mb-4">Privacy & Data</h2>
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              <strong>Your data is private.</strong> All contracts and questions are stored securely 
              and are only accessible by you.
            </p>
            <p>
              We do not share your data with third parties, publish it, or use it to train AI models.
            </p>
          </div>
        </div>

        {/* Export Data Section */}
        <div className="bg-card border border-border rounded-md p-6 mb-6">
          <h2 className="font-medium mb-4 flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export My Data
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Download a JSON file containing your profile, contracts metadata, and questions history.
          </p>
          <Button
            onClick={handleExport}
            disabled={exporting}
            variant="outline"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Download My Data"}
          </Button>
        </div>

        {/* Delete Account Section */}
        <div className="bg-card border border-destructive rounded-md p-6">
          <h2 className="font-medium mb-4 flex items-center gap-2 text-destructive">
            <Trash2 className="w-5 h-5" />
            Delete My Account
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <Button
            onClick={() => setShowDeleteDialog(true)}
            variant="destructive"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Account
          </Button>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Delete Account & Data?
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-4">
                <p className="text-destructive font-medium">
                  This will permanently delete:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Your account profile</li>
                  <li>All uploaded contracts</li>
                  <li>All questions and answers</li>
                  <li>All case documents (if any)</li>
                  <li>All saved data</li>
                </ul>
                <p className="text-destructive font-medium">
                  This action cannot be undone.
                </p>

                <div className="space-y-3 pt-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="confirm-delete"
                      checked={deleteConfirmed}
                      onCheckedChange={setDeleteConfirmed}
                    />
                    <label
                      htmlFor="confirm-delete"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      I understand this cannot be undone
                    </label>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Type <span className="font-mono bg-secondary px-1">DELETE</span> to confirm
                    </label>
                    <Input
                      type="text"
                      placeholder="Type DELETE"
                      value={deleteTyped}
                      onChange={(e) => setDeleteTyped(e.target.value)}
                    />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setDeleteConfirmed(false);
                setDeleteTyped("");
              }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={!deleteConfirmed || deleteTyped !== "DELETE" || deleting}
                className="bg-destructive hover:bg-destructive/90"
              >
                {deleting ? "Deleting..." : "Delete Everything"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Settings;
